import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';
import PromotionalBannerPreview from './PromotionalBannerPreview';

const PromotionalBannerList = ({ refreshKey, onEdit }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  useEffect(() => {
    fetchBanners();
  }, [refreshKey]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/promotional-banner/all`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      setBanners(response.data || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError('Failed to load promotional banners');
      toast.error('Failed to load promotional banners');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${API_BASE_URL}/api/promotional-banner/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      toast.success('Banner deleted successfully');
      fetchBanners();
    } catch (err) {
      console.error('Error deleting banner:', err);
      toast.error(err.response?.data?.error || 'Failed to delete banner');
    }
  };

  const handleToggle = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.patch(
        `${API_BASE_URL}/api/promotional-banner/${id}/toggle`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      const updatedBanner = response.data;
      toast.success(`Banner ${updatedBanner.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchBanners();
    } catch (err) {
      console.error('Error toggling banner:', err);
      toast.error(err.response?.data?.error || 'Failed to toggle banner status');
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      category_below: 'Category Below',
      home_middle: 'Home Middle',
      home_top: 'Home Top',
    };
    return labels[position] || position;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (banners.length === 0) {
    return (
      <div className="card shadow-sm border rounded-xl">
        <div className="card-body p-5 text-center">
          <p className="text-muted mb-0">No promotional banners found. Create your first banner to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      {banners.map((banner) => (
        <div key={banner.id} className="col-12 mb-4">
          <div className="card shadow-sm border rounded-xl">
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0 fw-semibold">{banner.title}</h5>
                <small className="text-muted">
                  Position: {getPositionLabel(banner.position)} | Priority: {banner.priority}
                  {banner.startDate && ` | Start: ${formatDate(banner.startDate)}`}
                  {banner.endDate && ` | End: ${formatDate(banner.endDate)}`}
                </small>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span
                  className={`badge ${banner.isActive ? 'bg-success' : 'bg-secondary'}`}
                >
                  {banner.isActive ? 'Active' : 'Inactive'}
                </span>
                <Button
                  color={banner.isActive ? "secondary" : "success"}
                  size="sm"
                  onClick={() => handleToggle(banner.id)}
                  className="rounded-pill"
                  style={{
                    width: '100px',
                    height: '32px',
                    padding: '0',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  {banner.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => onEdit(banner)}
                  className="rounded-pill"
                  style={{
                    width: '100px',
                    height: '32px',
                    padding: '0',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  Edit
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDelete(banner.id)}
                  className="rounded-pill"
                  style={{
                    width: '100px',
                    height: '32px',
                    padding: '0',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
            <div className="card-body">
              <PromotionalBannerPreview banner={banner} />
              
              {/* Additional Details */}
              <div className="mt-3 pt-3 border-top">
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted d-block">Action Type:</small>
                    <span>{banner.actionType || 'None'}</span>
                  </div>
                  {banner.actionUrl && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Action URL:</small>
                      <a href={banner.actionUrl} target="_blank" rel="noopener noreferrer" className="text-break">
                        {banner.actionUrl}
                      </a>
                    </div>
                  )}
                  {banner.registrationUrl && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Registration URL:</small>
                      <a href={banner.registrationUrl} target="_blank" rel="noopener noreferrer" className="text-break">
                        {banner.registrationUrl}
                      </a>
                    </div>
                  )}
                  {(banner.width || banner.height) && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Dimensions:</small>
                      <span>
                        {banner.width ? `${banner.width}px` : 'Auto'} × {banner.height ? `${banner.height}px` : 'Auto'}
                      </span>
                    </div>
                  )}
                  {banner.imageOpacity !== undefined && banner.imageOpacity !== null && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Image Opacity:</small>
                      <span>{banner.imageOpacity}</span>
                    </div>
                  )}
                  {banner.tags && banner.tags.length > 0 && (
                    <div className="col-md-6">
                      <small className="text-muted d-block">Tags:</small>
                      <div>
                        {banner.tags.map((tag, idx) => (
                          <span key={idx} className="badge bg-secondary me-1">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <small className="text-muted d-block">Target Audience:</small>
                    <span>{banner.targetAudience || 'All'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PromotionalBannerList;
